import {
  useRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  MutableRefObject,
} from 'react';
import ResizeObserver from 'resize-observer-polyfill';

/**
 * A custom hook that allows you to observe and respond to changes to a given
 * element's size.
 *
 * ```jsx
 * import useResizeObserver from '@packages/systems/ui/hooks/useResizeObserver';
 * import {useRef, useEffect, useState} from 'react';
 *
 * const ChangingNote = () => {
 *   const elRef = useRef(null);
 *   const [size, setSize] = useState({width: 0, height: 0});
 *   const onResize = entries => {
 *     const {width, height} = entries[0].contentRect;
 *     setSize({width, height})
 *   }
 *   const resizeObserver = useResizeObserver(onResize);
 *
 *   const setRef = el => {
 *     if (elRef.current == null && el) {
 *       resizeObserver.observe(el)
 *       elRef.current = el;
 *     }
 *   }
 *
 *   // Now if this <div /> element changes in size eg if more children are added
 *   // or its size CSS changes, `onResize` will fire and update accordingly
 *   return <div ref={setRef}>{...}</div>
 * }
 * ```
 */
export default function useResizeObserver(
  onResize: (entries: ResizeObserverEntry[]) => void
) {
  const resizeObserverRef: MutableRefObject<ResizeObserver | null> =
    useRef(null);

  // Tracks the nodes this hook is currently observing so it can release them
  // without relying solely on unmount. A long-lived consumer (e.g. most
  // same-origin canvas components) can otherwise pin a detached node forever:
  // it observes a node, the node is later replaced/removed on a page switch,
  // and nothing unobserves the old one until the component unmounts.
  const observedNodesRef: MutableRefObject<Set<HTMLElement>> = useRef(
    new Set()
  );

  const onResizeRef: MutableRefObject<typeof onResize | null> = useRef(null);

  // We need to reference onResize over ref, because new ResizeObserver(onResize)
  // will be cached over ref, so the callback we pass to ResizeObserver will have
  // a stale scope. This way we always call the latest onResize callback.
  useLayoutEffect(() => {
    onResizeRef.current = onResize;
  }, [onResize]);

  function getResizeObserver() {
    if (resizeObserverRef.current === null) {
      resizeObserverRef.current = new ResizeObserver(
        (entries: Array<ResizeObserverEntry>) => {
          if (!onResizeRef.current) return;
          onResizeRef.current(entries);
        }
      );
    }
    return resizeObserverRef.current;
  }

  useEffect(() => {
    return function cleanUpResizeObserver() {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
      resizeObserverRef.current = null;
      observedNodesRef.current.clear();
    };
  }, []);

  // Releases any previously observed nodes that are no longer connected to the
  // document. This runs whenever a consumer observes a node, so repeated
  // observe/replace cycles can't accumulate detached nodes — the predecessor
  // is dropped on the next observe rather than waiting for unmount.
  function releaseDetachedNodes() {
    if (resizeObserverRef.current === null) return;
    for (const node of observedNodesRef.current) {
      if (!node.isConnected) {
        resizeObserverRef.current.unobserve(node);
        observedNodesRef.current.delete(node);
      }
    }
  }

  return useMemo(
    () => ({
      observe: (node: HTMLElement, options?: ResizeObserverOptions) => {
        releaseDetachedNodes();
        observedNodesRef.current.add(node);
        getResizeObserver().observe(node, options);
      },
      unobserve: (node: HTMLElement) => {
        observedNodesRef.current.delete(node);
        getResizeObserver().unobserve(node);
      },
      disconnect: () => {
        observedNodesRef.current.clear();
        getResizeObserver().disconnect();
      },
    }),
    []
  );
}
